import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useLang } from "@/context/LanguageContext";
import { StageBadge, PriorityBadge, StatusBadge } from "@/components/Badges";
import { deptLabel } from "@/lib/constants";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Search, Phone } from "lucide-react";
import { format } from "date-fns";

export default function SearchPage() {
  const [mobile, setMobile] = useState("");
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const { lang } = useLang();

  async function search() {
    if (!/^\d{10}$/.test(mobile)) {
      setResults({ items: [], error: "Enter a valid 10-digit mobile" });
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.get("/complaints", {
        params: { mobile, limit: 100 },
      });
      setResults(data);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="max-w-4xl mx-auto space-y-8" data-testid="search-page">
      <div>
        <div className="text-xs uppercase tracking-[0.18em] text-orange-600 font-semibold mb-2">
          Track Complaints
        </div>
        <h1 className="text-3xl font-bold text-slate-900 tracking-tight">
          Search by mobile number
        </h1>
        <p className="text-slate-500 text-sm mt-2">
          Every complaint filed with the same mobile number will appear below.
        </p>
      </div>

      <div className="bg-white border border-slate-200 rounded-lg p-6">
        <div className="flex items-center gap-3">
          <div className="relative flex-1">
            <Phone className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input
              data-testid="search-mobile-input"
              value={mobile}
              onChange={(e) =>
                setMobile(e.target.value.replace(/[^0-9]/g, "").slice(0, 10))
              }
              onKeyDown={(e) => e.key === "Enter" && search()}
              placeholder="Enter 10-digit mobile number"
              className="h-12 pl-10 font-mono-cms text-lg tracking-wider"
            />
          </div>
          <Button
            onClick={search}
            data-testid="search-mobile-btn"
            disabled={loading}
            className="h-12 px-6 bg-orange-600 hover:bg-orange-700 text-white font-semibold"
          >
            <Search className="w-4 h-4 mr-2" />
            {loading ? "Searching..." : "Search"}
          </Button>
        </div>
      </div>

      {results && (
        <div data-testid="search-results">
          {results.error && (
            <div className="text-red-600 text-sm bg-red-50 border border-red-200 rounded-md p-3">
              {results.error}
            </div>
          )}
          {!results.error && (
            <>
              <div className="text-sm text-slate-500 mb-3">
                Found{" "}
                <span className="font-semibold text-slate-900">
                  {results.total || 0}
                </span>{" "}
                complaint(s) for{" "}
                <span className="font-mono-cms font-semibold text-slate-900">
                  {mobile}
                </span>
              </div>
              <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
                <table className="w-full">
                  <thead className="bg-slate-50 border-b border-slate-200">
                    <tr className="text-left text-[11px] font-bold text-slate-500 uppercase tracking-wider">
                      <th className="py-3 px-4">No.</th>
                      <th className="py-3 px-4">Applicant</th>
                      <th className="py-3 px-4">Subject</th>
                      <th className="py-3 px-4">Department</th>
                      <th className="py-3 px-4">Stage</th>
                      <th className="py-3 px-4">Status</th>
                      <th className="py-3 px-4">Priority</th>
                      <th className="py-3 px-4">Submitted</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(results.items || []).map((c) => (
                      <tr
                        key={c.id}
                        data-testid={`search-row-${c.id}`}
                        className="border-b border-slate-100 hover:bg-slate-50"
                      >
                        <td className="py-3 px-4">
                          <Link
                            to={`/complaints/${c.id}`}
                            className="font-mono-cms text-xs text-orange-600 font-semibold hover:underline"
                          >
                            {c.complaint_no}
                          </Link>
                        </td>
                        <td className="py-3 px-4 text-sm font-semibold text-slate-800">
                          {c.applicant_name}
                        </td>
                        <td className="py-3 px-4 text-sm text-slate-700 max-w-xs truncate">
                          {c.subject}
                        </td>
                        <td className={`py-3 px-4 text-sm text-slate-700 ${lang === "hi" ? "font-devanagari" : ""}`}>
                          {deptLabel(c.department, lang)}
                        </td>
                        <td className="py-3 px-4">
                          <StageBadge stage={c.current_stage} />
                        </td>
                        <td className="py-3 px-4">
                          <StatusBadge status={c.status} />
                        </td>
                        <td className="py-3 px-4">
                          <PriorityBadge priority={c.priority} />
                        </td>
                        <td className="py-3 px-4 text-xs text-slate-500 whitespace-nowrap">
                          {format(new Date(c.created_at), "dd MMM yyyy")}
                        </td>
                      </tr>
                    ))}
                    {(results.items || []).length === 0 && (
                      <tr>
                        <td colSpan={8} className="py-10 text-center text-slate-500 text-sm">
                          No complaints found for this mobile number.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
